import { useEffect, useRef, useState } from 'react';
import { useAuiState, type ToolCallMessagePartStatus } from '@assistant-ui/react';
import {
  CheckIcon,
  CircleAlertIcon,
  LoaderCircleIcon,
  MinusIcon,
  PauseIcon,
  SparklesIcon,
} from 'lucide-react';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { activityOutcome } from './tool-presentation';
import type { ThreadGroupPart } from './thread';
import { useOptionalWorkspace } from '@/workspace/context';
import {
  activityFailure,
  activityPhrases,
  answerCitations,
  earlierAttempts,
} from './activity-summary';
import { WorkspaceIcon } from '@/workspace/icons';
import { formatCount } from '@/i18n/format';
import { activityTargetFor } from './activity-navigation';
import { countedActivity } from './activity-verbs';
import { useNamespaceCopy } from '@/i18n/ns';
import appReopenCopy from '@/i18n/ns/app-reopen';
import { latestAppPreview } from '@/workspace/apps/preview/reopen';

export function ToolActivity({
  group,
  sourceNote,
  reasoningOnly = false,
  closed = false,
}: {
  group: ThreadGroupPart;
  sourceNote?: React.ReactNode;
  reasoningOnly?: boolean;
  /** Steps before a message read mid-work: done, even while the answer runs on. */
  closed?: boolean;
}) {
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const messageParts = useAuiState((s) => s.message.parts);
  const messageId = useAuiState((s) => s.message.id);
  const messageStatus = useAuiState((s) => s.message.status?.type);
  const workspace = useOptionalWorkspace();
  const reopen = useNamespaceCopy(appReopenCopy);
  const canvas = workspace?.canvas as
    | { kind?: string; id?: string; tab?: string; activityMessageId?: string }
    | null
    | undefined;
  const active =
    (canvas?.kind === 'activity' && canvas.id === messageId) ||
    (canvas?.tab === 'activity' && canvas.activityMessageId === messageId);
  const parts = group.indices.flatMap((index) => {
    const part = messageParts[index];
    return part?.type === 'tool-call'
      ? [{ ...part, status: part.status as ToolCallMessagePartStatus }]
      : [];
  });
  // DES1 HC-10: the line states each step's last outcome. A failed attempt that a later
  // call of the same thing completed stays in the steps, counted in a muted note.
  const earlier = earlierAttempts(parts);
  const settled = parts.filter((part) => !earlier.has(part));
  const outcome = reasoningOnly ? 'complete' : activityOutcome(settled);
  const citedSources = answerCitations(messageParts);
  const needsAttention = outcome === 'approval';
  const runningPart = [...parts].reverse().find((part) => part.status?.type === 'running');
  const writing = !closed && messageStatus === 'running' && parts.length > 0 && !runningPart;
  const runningPhrase = runningPart
    ? activityPhrases([runningPart], locale, parts)[0]?.verb.running
    : undefined;
  const [shownPhrase, setShownPhrase] = useState(runningPhrase);
  const shownAt = useRef(0);
  useEffect(() => {
    if (!runningPhrase || runningPhrase === shownPhrase) return;
    const wait = Math.max(0, 600 - (performance.now() - shownAt.current));
    const timer = window.setTimeout(() => {
      shownAt.current = performance.now();
      setShownPhrase(runningPhrase);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [runningPhrase, shownPhrase]);
  const phrases = activityPhrases(settled, locale, parts);
  const summary =
    phrases
      .slice(0, 2)
      // A repeat is counted in the reader's grammar ("Read 3 files"); a verb with no
      // noun stays as it is, and the steps list shows each call (DES1 #3521, HC-08).
      .map((phrase) =>
        phrase.count > 1 && phrase.verb.counted
          ? countedActivity(phrase.verb.counted, phrase.count, locale)
          : phrase.label,
      )
      .join(' · ') || (reasoningOnly ? copy.activityThinking : copy.toolActivity);
  const citationCount = citedSources;
  // A failure names the step that failed first, then counts what completed.
  const failure = outcome === 'error' ? activityFailure(settled, locale, parts) : null;
  const failedPhrase = failure?.label ?? copy.activityCouldNotFinish;
  const completedNote =
    failure && failure.completed > 0
      ? ` · ${formatCount(copy.activityStepsDone, failure.completed, locale)}`
      : '';
  // One calm line: the live verb while working, one past-tense summary once done.
  // Counts and tool names stay in the Activity view, one tap away.
  const showsFailure = !reasoningOnly && !needsAttention && !writing && outcome === 'error';
  const statusLabel = reasoningOnly
    ? copy.activityShowThinking
    : needsAttention
      ? copy.activityWaiting
      : writing
        ? copy.activityWriting
        : outcome === 'running'
          ? `${shownPhrase ?? copy.toolRunning}…`
          : showsFailure
            ? `${failedPhrase}${completedNote}`
            : outcome === 'cancelled' || outcome === 'incomplete'
              ? `${copy.activityStopped} · ${summary}`
              : summary;
  const earlierNote =
    earlier.size > 0 && outcome !== 'running' && !reasoningOnly
      ? formatCount(copy.toolEarlierAttemptsFailed, earlier.size, locale)
      : '';
  const Glyph = reasoningOnly
    ? SparklesIcon
    : needsAttention
      ? PauseIcon
      : outcome === 'running'
        ? LoaderCircleIcon
        : outcome === 'error'
          ? CircleAlertIcon
          : outcome === 'cancelled' || outcome === 'incomplete'
            ? MinusIcon
            : outcome === 'complete'
              ? CheckIcon
              : SparklesIcon;
  // A finished Chat-built App: its message keeps the preview output, so the App can be reopened from here with the
  // existing opener (the Activity line above stays the separate way into the steps).
  const appPreview = latestAppPreview(
    group.indices.map(
      (index) => messageParts[index] as { type?: string; toolName?: string; result?: unknown },
    ),
    workspace?.threadId,
  );
  const showsApp =
    appPreview &&
    !reasoningOnly &&
    outcome !== 'running' &&
    messageStatus !== 'running' &&
    !(canvas?.kind === 'app-preview' && canvas.id === appPreview.id);
  return (
    <>
      <button
        type="button"
        className="pc-activity-line"
        data-slot="tool-group-trigger"
        data-outcome={outcome}
        data-active={active ? 'true' : 'false'}
        aria-expanded={active ? 'true' : 'false'}
        aria-label={`${statusLabel}${earlierNote ? ` · ${earlierNote}` : ''}. ${copy.activityOpen}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          workspace?.openCanvas(activityTargetFor(workspace.canvas, messageId, messageParts));
        }}
      >
        <Glyph aria-hidden className="pc-activity-line-glyph" />
        {/* Keyed so each new verb crossfades in place instead of snapping. */}
        <span className="pc-activity-line-summary" key={statusLabel}>
          {showsFailure ? (
            <>
              <span className="pc-activity-line-failed">{failedPhrase}</span>
              {completedNote}
            </>
          ) : (
            statusLabel
          )}
        </span>
        {earlierNote && (
          <span className="pc-activity-line-earlier">
            <span className="pc-activity-line-dot"> · </span>
            {earlierNote}
          </span>
        )}
        {outcome !== 'running' && citationCount.length > 0 && (
          <span className="pc-activity-line-sources">
            · {new Intl.NumberFormat(locale).format(citationCount.length)} {copy.activitySources}
          </span>
        )}
        {sourceNote}
        <WorkspaceIcon name="chevron" size={12} className="pc-activity-line-arrow" />
      </button>
      {showsApp && (
        <button
          type="button"
          className="pc-activity-line pc-activity-open-app"
          data-testid="open-app-preview"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            workspace?.openCanvas(appPreview);
          }}
        >
          {reopen.openApp}
        </button>
      )}
    </>
  );
}
